import { Map } from 'maplibre-gl';
import * as d3 from "d3";
import { FeatureCollection, Geometry } from "geojson";
import { LayerParams } from "../../map/layer-manager";
import { NormalizerAux } from "../../services/normalizer-aux/normalizer-aux";
import { MapLayer } from "../map-layer";

/**
 * Camada dos polígonos do diagrama de Voronoi.
 * Os polígonos são criados de forma que cada polígono tem 1 ou 0 estação.
 * Cada polígono conta as ocorrências que ocorreram próximas a cada estação.
 */
export class VoronoiLayer extends MapLayer {

  constructor(private sourceId: string, private layerId: string) {
    super();
  }

  hasData(layerParams: LayerParams): boolean {
    let { middleCounts } = layerParams;
    return middleCounts.length > 0;
  }

  remove(map: Map) {

    if (map.getLayer(this.layerId)) {
      map.removeLayer(this.layerId)
    }

    if (map.getSource(this.sourceId)) {
      map.removeSource(this.sourceId);
    }
  }


  /**
   * Alterna entre exibir / esconder a layer dos polígonos do diagrama de Voronoi.
   */
  show(map: Map, layerParams: LayerParams) {

    let { voronoiEventsCount: eventsCount, middleCounts, voronoiData } = layerParams;

    if (middleCounts.length == 0) {
      this.setMapSource(map, this.sourceId, voronoiData);
      this.setMapLineLayer(map, this.sourceId, this.layerId, '#1155FF', 1, 1);
      return;
    }

    // 1) definir a escala usando os números de ocorrências intermediários
    let normalizer = NormalizerAux.createNormalizer(
      middleCounts,
      (count) => count
    );

    // 2) colorir cada célula de voronoi com a escala
    let colorScale = d3.interpolatePuRd;
    voronoiData.features.forEach(feature => {

      let eventCount = eventsCount.find(
        row => feature.properties.id_estacao == row.id_estacao
      );
      feature.properties.color = (eventCount == null) ? colorScale(0) : colorScale(normalizer(eventCount.qtd_ocorrencias));
    })

    // 3) adicionar source e layer novas
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: voronoiData
    });

    map.addLayer({
      id: this.layerId,
      source: this.sourceId,
      type: 'fill',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.6,
        'fill-outline-color': '#8811cc'
      }
    });

  }

  /**
   * Set mapbox instance data source
   * @see https://docs.mapbox.com/mapbox-gl-js/example/geojson-polygon/
   * @param map mapbox instance which will be configured
   * @param dataSourceId data source exclusive identifier
   * @param dataSourceUrl data endpoint url from which data will be fetched
   */
  setMapSource(map: Map, dataSourceId: string, dataSourceUrl: FeatureCollection<Geometry>) {
    // first, we add a data source with 'data' attribute pointing to some data end point
    map.addSource(dataSourceId, {
      type: 'geojson',
      data: dataSourceUrl,
      promoteId: 'ogc_fid'
    });
  }


  /**
   * Set mapbox instance line layer
   * @see https://docs.mapbox.com/mapbox-gl-js/example/geojson-polygon/
   * @param map mapbox instance which will be configured
   * @param dataSourceId data source exclusive identifier
   * @param dataLayerLineId line type layer exclusive identifier
   * @param lineColor layer line color
   * @param lineOpacity layer line opacity
   * @param lineWidth layer line width
   */
  setMapLineLayer(map: Map, dataSourceId: string, dataLayerLineId: string,
    lineColor: string, lineOpacity: number, lineWidth: number) {
    // lastly, we add a type outline layer to draw the boundaries of the reigions
    if (map.getLayer(dataLayerLineId)) {
      map.removeLayer(dataLayerLineId);
    }

    map.addLayer({
      id: dataLayerLineId,
      type: 'line',
      source: dataSourceId,
      layout: {
        visibility: 'visible',
      },
      paint: {
        'line-color': lineColor,
        'line-opacity': 1,
        'line-width': lineWidth,
      },
    });
  }

}
